/* ------------------------------------------------------------------
 * Mattress Overstock — Casper "Estimated delivery" badge
 *
 * Each slot carries ALL of its product's variant ids. We batch-fetch them from
 * the shopify-rv-eta service (Railway) over CORS and, by default, show the
 * SOONEST in-stock date (min customer_date among displayable variants). On a
 * PDP, once the shopper selects a size we show THAT variant's date instead.
 *
 *   batch: POST /api/eta/batch { items:[{shopify_variant_id}, ...] }
 *   -> { results:[{ shopify_variant_id, displayable, visible_label, customer_date }] }
 *
 * Fail-safe by construction: any network error, timeout, non-200, bad JSON, or
 * no-displayable-variant renders NOTHING (the slot stays hidden) and never
 * throws to the page. Slots are server-rendered (Casper-only, [hidden]); this
 * script only toggles a class and sets textContent.
 *
 * Vanilla IIFE — not bundled, gate edits with `node --check`.
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  var API_BASE = 'https://shopify-rv-eta-production.up.railway.app';
  var TIMEOUT_MS = 6000;
  var resultsById = {}; // shopify_variant_id (string) -> { displayable, visible_label, customer_date }

  function fetchJson(url, options) {
    options = options || {};
    var controller = ('AbortController' in window) ? new AbortController() : null;
    var timer = null;
    if (controller) {
      options.signal = controller.signal;
      timer = setTimeout(function () { try { controller.abort(); } catch (e) {} }, TIMEOUT_MS);
    }
    return fetch(url, options).then(function (res) {
      if (timer) { clearTimeout(timer); timer = null; }
      if (!res.ok) throw new Error('mo-eta http ' + res.status);
      return res.json();
    }, function (err) {
      if (timer) { clearTimeout(timer); timer = null; }
      throw err;
    });
  }

  function reveal(el, label) {
    if (!el || !label) return;
    var t = el.querySelector('.mo-eta__text');
    if (!t) return;
    t.textContent = String(label); // exact customer string; textContent = injection-safe
    el.removeAttribute('hidden');
    el.classList.add('is-ready');
  }

  function conceal(el) {
    if (!el) return;
    el.classList.remove('is-ready');
    el.setAttribute('hidden', '');
    var t = el.querySelector('.mo-eta__text');
    if (t) t.textContent = '';
  }

  function idsOf(el) {
    return (el.getAttribute('data-mo-eta-variants') || '')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  // Soonest displayable result among the given ids. customer_date is ISO
  // (YYYY-MM-DD), so lexical comparison is chronological.
  function soonest(ids) {
    var best = null;
    for (var i = 0; i < ids.length; i++) {
      var r = resultsById[ids[i]];
      if (r && r.displayable && r.visible_label && r.customer_date) {
        if (!best || String(r.customer_date) < String(best.customer_date)) best = r;
      }
    }
    return best;
  }

  function renderCard(el) {
    var best = soonest(idsOf(el));
    if (best) reveal(el, best.visible_label); else conceal(el);
  }

  // PDP: until a size is chosen show the soonest in-stock date; once chosen,
  // show that size's date (or hide if that size has no displayable ETA).
  // The slot carries only PURCHASABLE variant ids (OOS policy 2026-07-01), so a
  // selected variant not in the slot's list is sold out ⇒ conceal.
  function renderPdp(el) {
    var sel = el._moEtaSelected;
    if (sel === undefined) {
      var best = soonest(idsOf(el));
      if (best) reveal(el, best.visible_label); else conceal(el);
      return;
    }
    if (sel == null || idsOf(el).indexOf(String(sel)) === -1) { conceal(el); return; }
    var r = resultsById[String(sel)];
    if (r && r.displayable && r.visible_label) reveal(el, r.visible_label); else conceal(el);
  }

  function render(el) {
    if (el.getAttribute('data-mo-eta-context') === 'pdp') renderPdp(el); else renderCard(el);
  }

  // Fetch any not-yet-cached ids; resolves once resultsById is populated.
  function ensure(ids) {
    var need = [];
    for (var i = 0; i < ids.length; i++) {
      var id = ids[i];
      if (id && !(id in resultsById) && need.indexOf(id) === -1) need.push(id);
    }
    if (!need.length) return Promise.resolve();
    return fetchJson(API_BASE + '/api/eta/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: need.map(function (id) { return { shopify_variant_id: String(id) }; }) })
    }).then(function (data) {
      var results = (data && data.results) || [];
      results.forEach(function (r) {
        if (r && r.shopify_variant_id != null) resultsById[String(r.shopify_variant_id)] = r;
      });
    });
  }

  function process(els) {
    if (!els.length) return;
    var all = [];
    els.forEach(function (el) {
      el.setAttribute('data-mo-eta-state', 'pending');
      all = all.concat(idsOf(el));
    });
    ensure(all).then(function () {
      els.forEach(function (el) { render(el); el.setAttribute('data-mo-eta-state', 'done'); });
    }).catch(function () {
      els.forEach(function (el) { conceal(el); el.setAttribute('data-mo-eta-state', 'done'); });
    });
  }

  function collect(root) {
    return Array.prototype.slice.call(
      (root || document).querySelectorAll('[data-mo-eta]:not([data-mo-eta-state])')
    );
  }

  // The theme's `product:variant-change` event carries a STALE variant (the new
  // one resolves later, inside an async section-render .then), so we instead read
  // the canonical hidden id input — the theme sets its value and fires 'change' on
  // it AFTER resolving the new variant. Scoped to the main product container so
  // recommendation / quick-add forms can't be mistaken for the PDP selection.
  function initPdpVariantSync() {
    var pdp = document.querySelector('[data-mo-eta][data-mo-eta-context="pdp"]');
    if (!pdp) return;
    var scope = pdp.closest('.thb-product-detail') || document;
    // Deep links (?variant=…) arrive with a size already selected before any change
    // event fires; treat that as a selection so a sold-out preselected size never
    // shows the product's soonest date next to a Sold Out button. Without ?variant=
    // the theme's default preselection keeps the soonest-date behavior.
    try {
      if (/[?&]variant=/.test(window.location.search)) {
        var cur = scope.querySelector('input[name="id"]');
        var curId = cur && (cur.value || '').trim();
        if (curId) pdp._moEtaSelected = curId;
      }
    } catch (e) { /* non-fatal */ }
    Array.prototype.forEach.call(scope.querySelectorAll('input[name="id"]'), function (inp) {
      inp.addEventListener('change', function () {
        try {
          var id = (inp.value || '').trim();
          pdp._moEtaSelected = id || null;
          if (id && !(id in resultsById)) {
            ensure([id]).then(function () { renderPdp(pdp); }).catch(function () { conceal(pdp); });
          } else {
            renderPdp(pdp);
          }
        } catch (e) { /* non-fatal */ }
      });
    });
  }

  function init() {
    try { initPdpVariantSync(); } catch (e) { /* non-fatal */ }
    try { process(collect(document)); } catch (e) { /* non-fatal */ }
    // Cover cards injected after load (recommendations, infinite scroll, filtering).
    try {
      if (!('MutationObserver' in window) || !document.body) return;
      var scheduled = false;
      var observer = new MutationObserver(function (mutations) {
        if (scheduled) return;
        var found = false;
        for (var i = 0; i < mutations.length && !found; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType === 1 && ((n.matches && n.matches('[data-mo-eta]')) ||
                (n.querySelector && n.querySelector('[data-mo-eta]')))) { found = true; break; }
          }
        }
        if (!found) return;
        scheduled = true;
        setTimeout(function () { scheduled = false; process(collect(document)); }, 150);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (e) { /* non-fatal */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
